import Helpers from '../helpers';
import Targeting from './targeting';
import Prebid from './prebid';

/**
 * Import Prebid and required modules
 * https://github.com/prebid/Prebid.js/blob/master/README.md
 * https://docs.prebid.org/download.html
 **/
import pbjs from 'prebid.js';

import 'prebid.js/modules/consentManagement';
import 'prebid.js/modules/fpdModule';
import 'prebid.js/modules/gdprEnforcement';
import 'prebid.js/modules/gptPreAuction';
import 'prebid.js/modules/currency';

import 'prebid.js/modules/enrichmentFpdModule';
import 'prebid.js/modules/brandmetricsRtdProvider';

import 'prebid.js/modules/userId';
import 'prebid.js/modules/sharedIdSystem';
import 'prebid.js/modules/lotamePanoramaIdSystem';
import 'prebid.js/modules/uid2IdSystem';

import 'prebid.js/modules/appnexusBidAdapter';
import 'prebid.js/modules/ixBidAdapter';
import 'prebid.js/modules/criteoBidAdapter';
import 'prebid.js/modules/ozoneBidAdapter';
import 'prebid.js/modules/adyoulikeBidAdapter';
import 'prebid.js/modules/improvedigitalBidAdapter';
import 'prebid.js/modules/oguryBidAdapter';

/** Conditional Imports */

////////////////////////////
///////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////
//////////

///////////////////////////
/////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////
//////////

//////////////////////////
           import { prebidConfig } from '../inc/config/puzzler';
           import { responsiveSlotPlacements } from '../inc/placements/responsive/puzzler';
           import { responsiveSlotDefinitions } from '../inc/slots/responsive/wpcluster';
           import { fluidSlotDefinitions } from '../inc/slots/fluid/puzzler';
//////////

/** Expose Prebid API */
window.pbjs = pbjs;

export default class GPT {

  /**
   * Inserts the Google Publisher Tag script (gpt.js)
   */
  static insert = () => {
    const element = document.createElement( 'script' );
    const url     = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
    element.async = true;
    element.type  = 'text/javascript';
    element.src   = url;
    document.getElementsByTagName( 'head' )[0].appendChild( element );
  };

  /**
   * Runs the top level advertising logic
   *
   * @param tcData - The users consent data object
   * @returns {Promise<void>}
   */
  static enable = async ( tcData ) => {

    // Insert GPT (Google Publisher Tag Script)
    this.insert();

    // Check if we have consent for personalised ads (returns true if we have consent) usage ref: https://support.google.com/admanager/answer/7678538?hl=en
    const personalisedAdsAllowed = await Helpers.isPersonalisedAllowed( tcData );

    // Wait for the googletag API to be ready
    await Helpers.googletagLoaded( 10000 );
    console.log( 'GPT Loaded' );

    // Process Prebid Queue (Required)
    pbjs.processQueue();

    // Set Prebid Config
    let config = prebidConfig;
    if ( ADVERTISING_PAGE_DATA.debug === 'true' ) {
      config.debug = true;
    }
    pbjs.setConfig( config );

    /** Start native slot placement ( WPC Cluster only ) */
////////////////////////////////
////////////////////////////////////////////////////////////////
//////////////

    // User activity tracking
    window.userActivityTimeout = 120;
    window.userLastActivity    = Math.floor( Date.now() / 1000 );

    // Init user activity listeners
    document.addEventListener( 'mousemove', Helpers.userActivity, {passive: true} );
    document.addEventListener( 'mousedown', Helpers.userActivity, {passive: true} );
    document.addEventListener( 'keypress', Helpers.userActivity, {passive: true} );
    document.addEventListener( 'touchmove', Helpers.userActivity, {passive: true} );
    document.addEventListener( 'gesture', Helpers.userActivity, {passive: true} );
    document.addEventListener( 'deviceorientation', Helpers.userActivity, {passive: true} );
    document.addEventListener( 'swipe', Helpers.userActivity, {passive: true} );

    // Set legacy dynamic page targeting
    ADVERTISING_PAGE_DATA['inskin_yes'] = (screen.width >= 1255 ? 'true' : 'false');
    ADVERTISING_PAGE_DATA['pses_yes']   = (screen.width >= 1140 ? 'true' : 'false');

    // Get the on page slot divs
    const adSlotDivs = document.querySelectorAll( 'div[rel="advert"]' );

    // Set slot targeting info
    let adSlots = await Targeting.setDefaults( adSlotDivs, responsiveSlotPlacements, responsiveSlotDefinitions );

    // Prepare the formatted slot data for submission to prebid, excluding out of page slots
    let formattedAdSlots = await Prebid.prepSlotsForAuction( adSlots, fluidSlotDefinitions );

    googletag.collapsable = true;
    googletag.zone        = ADVERTISING_PAGE_DATA.zone;

    googletag.pubads().disableInitialLoad();

    // Pass the formatted ad slots to prebid excluding off page slots
    pbjs.addAdUnits( formattedAdSlots );

    let viewport = Helpers.getViewportDimentions();

    // For each slot
    for ( let i = 0; i < adSlots.length; i++ ) {

      // Define the slot
      if ( adSlots[i].type === 'legacy' ) {
        adSlots[i].gSlotDef = googletag
          .defineSlot( ADVERTISING_PAGE_DATA.zone, adSlots[i].sizes, adSlots[i].id );
      } else if ( adSlots[i].type === 'fluid-native' ) {
        adSlots[i].gSlotDef = googletag
          .defineSlot( ADVERTISING_PAGE_DATA.zone, 'fluid', adSlots[i].id );
      } else {
        adSlots[i].gSlotDef = googletag
          .defineSlot( ADVERTISING_PAGE_DATA.zone, adSlots[i].bidSizes.arrays, adSlots[i].id )
          .defineSizeMapping( adSlots[i].mappings );
      }

      // Set slot level targeting values for all slots
      adSlots[i].gSlotDef
        .setTargeting( 'type', adSlots[i].type )
        .setTargeting( 'position', adSlots[i].position )
        .setTargeting( 'viewport_width', viewport.width )
        .setTargeting( 'viewport_height', viewport.height );

      // Set slot_id parameter for fluid slots
      if ( adSlots[i].type === 'fluid-native' ) {
        adSlots[i].gSlotDef
          .setTargeting( 'slot_id', adSlots[i].id );
      }

      // Set legacy targeting values if they are defined
      if ( adSlots[i].legacyTargeting ) {
        for ( let ii = 0; ii < adSlots[i].legacyTargeting.length; ++ii ) {
          adSlots[i].gSlotDef.setTargeting( adSlots[i].legacyTargeting[ii].key, adSlots[i].legacyTargeting[ii].value );
        }
      }

      // Enable companion ad service ( excluding off page and native slots )
      if ( ! adSlots[i].offPage && adSlots[i].type !== 'fluid-native' ) {
        adSlots[i].gSlotDef
          .addService( googletag.companionAds() );
      }

      // Enable pubads service
      adSlots[i].gSlotDef
        .addService( googletag.pubads() );

      // Request bids for on-page slots & refresh when an ad slot is entering the viewport
      Helpers.observedElementInView( '#' + adSlots[i].id, async function () {
        GPT.requestBids( adSlots[i] );
      }, 1 );

    }

    // setRequestNonPersonalizedAds true if personalised ads are not allowed otherwise set false
    if ( ! personalisedAdsAllowed ) {
      googletag.pubads().setRequestNonPersonalizedAds( 1 );
    }

    // Set page targeting values
    for ( const [ key, value ] of Object.entries( ADVERTISING_PAGE_DATA ) ) {
      googletag.pubads().setTargeting( key, value );
    }

    // Set permutive targeting values
    if ( googletag.pubads().getTargeting( 'permutive' ).length === 0 ) {
      let kvs = localStorage.getItem( '_pdfps' );
      googletag.pubads().setTargeting( 'permutive', kvs ? JSON.parse( kvs ) : [] );
    }

    // Define Off Page (As per legacy implementation)
    googletag.defineOutOfPageSlot( ADVERTISING_PAGE_DATA.zone );
    // Enable single request
    googletag.pubads().enableSingleRequest();
    // Enable collapsing of empty ad slots

    // Force centering of ads
    googletag.pubads().setCentering( true );
    // Enable services
    googletag.enableServices();

    // Start ad refresh logic (refreshes in view ads after a period of user inactivity)
    googletag.pubads().addEventListener( 'impressionViewable', function ( event ) {

      let elementId = event.slot.getSlotElementId();

      for ( let i = 0; i < adSlots.length; i++ ) {

        if ( adSlots[i].id === elementId ) {

          // Don't refresh fluid or off page slots (causes too much layout shift)
          if ( adSlots[i].type !== 'fluid-native' && ! adSlots[i].offPage && ! adSlots[i].observed ) {

            GPT.adObserver( adSlots[i] );
            adSlots[i].observed = true;

          }

        }

      }
    } );

    // Hide unfilled native slots
    googletag.pubads().addEventListener( 'slotRenderEnded', function ( event ) {

      let slot    = event.slot;
      let id      = slot.getSlotElementId();
      let element = document.getElementById( id );
      let type    = element.getAttribute( 'data-slot-type' );
      let style   = element.getAttribute( 'data-slot-style' );
      let sizes   = element.getAttribute( 'data-ad-sizes' );
      let empty   = event.isEmpty;

      if ( empty ) {
        // If no ad was rendered into the fluid slot hide it and un-hide the original article block, excluding directly placed slots
        if ( type === 'fluid-native' ) {
          element.parentNode.style.display = 'none';
          if ( style !== 'direct-placement' ) {
            element.parentNode.previousSibling.style.display = 'block';
          }
        } else if ( sizes !== '1x1' && sizes !== '2x2' ) {
          // handles the case of ads failing to load when googletag.pubads().collapseEmptyDivs( true ) isn't used.
          element.style.display = 'none';
        }
      }
    } );

    // Force open/refresh GAM console if demo
    // https://docs.prebid.org/troubleshooting/troubleshooting-guide.html#verify-ad-unit-setup
    if ( typeof DEMO !== 'undefined' && DEMO ) {
      googletag.openConsole();
    }

  };

  /**
   * Tests if a particular adSlot is currently in view
   *
   * @param adSlot
   */
  static adObserver( adSlot ) {

    const REFRESH_SECONDS       = 30;
    const REFRESH_VARIANCE      = 2;
    const ACTIVE_VIEW_THRESHOLD = 0.9;

    let observer = new IntersectionObserver( function ( entries, observer ) {
      entries.forEach( entry => {
        if ( entry.isIntersecting ) {
          entry.target.refreshInterval = window.setInterval( function () {
            if ( Helpers.userIsActive() ) {
              GPT.requestBids( adSlot );
            }
          }, REFRESH_SECONDS * 1000 + Math.floor( Math.random() * REFRESH_VARIANCE * 1000 ) );
        } else {
          clearInterval( entry.target.refreshInterval );
        }
      } );
    }, {
      rootMargin: '-50px 0px 20px 0px',
      threshold: ACTIVE_VIEW_THRESHOLD
    } );
    observer.observe( document.getElementById( adSlot.id ) );
  }

  /**
   * Sends the auction request to Prebid & calls refresh when bids are returned
   *
   * @param adSlot
   */
  static requestBids( adSlot ) {
    pbjs.requestBids( {
      timeout: 1500,
      adUnitCodes: [ adSlot.id ],
      bidsBackHandler: function () {
        pbjs.setTargetingForGPTAsync( [ adSlot.id ] );
        googletag.pubads().refresh( [ adSlot.gSlotDef ] );
      }
    } );
  }

}